import type { FerryVehicleType } from '@my/ferry-api';
import type { FerryVehicleTypeMap, FerryVehicleTypeMapping } from './catalogue';

export interface DerivedVehicleType {
  /** Empty when the carrier file maps nothing for this booking: the save then refuses to send. */
  code: string;
  lengthMetres: string;
}

/**
 * The mapping lines to try, most specific first: each equipment key of the
 * cargo exactly, then its group (`trailer.reefer.single_temp` →
 * `trailer.reefer`), then the TMS booking type. The class alone (`trailer`)
 * is never tried: the booking type already says that much, and a class line
 * would override the container booking types.
 */
function candidates(
  map: FerryVehicleTypeMap,
  equipmentKeys: readonly string[],
  bookingType: string,
): FerryVehicleTypeMapping[] {
  const found: FerryVehicleTypeMapping[] = [];
  for (const key of equipmentKeys) {
    const parts = key.split('.');
    for (let n = parts.length; n >= 2; n--) {
      const mapping = map[parts.slice(0, n).join('.')];
      if (mapping) {
        found.push(mapping);
        break;
      }
    }
  }
  const byType = map[bookingType];
  if (byType) found.push(byType);
  return found;
}

/**
 * The carrier's vehicle type for a booking, the way the old desktop's
 * ShippingIntegration table chose it: the carrier file's line for the
 * cargo's equipment, else for the TMS booking type; its accompanied variant
 * when the driver crosses, its reefer variant for temperature-controlled
 * cargo; each checked against the carrier's active types. Nothing mapped
 * means nothing sent.
 */
export function defaultVehicleType(args: {
  map: FerryVehicleTypeMap | null;
  equipmentKeys: readonly string[];
  bookingType: string;
  accompanied: boolean;
  reefer: boolean;
  types: readonly FerryVehicleType[];
}): DerivedVehicleType {
  const active = args.types.filter((t) => t.isActive);
  const known = (code: string | null | undefined) =>
    code && active.some((t) => t.code === code) ? code : null;

  for (const mapping of candidates(args.map ?? {}, args.equipmentKeys, args.bookingType)) {
    const base =
      args.accompanied && mapping.accompaniedCode ? mapping.accompaniedCode : mapping.code;
    const code =
      known(args.reefer && mapping.reeferCode ? mapping.reeferCode : base) ?? known(base);
    if (code) {
      const lengthMetres =
        code === mapping.code && mapping.lengthMetres ? String(mapping.lengthMetres) : '';
      return { code, lengthMetres };
    }
  }
  return { code: '', lengthMetres: '' };
}
